import { safeLocalStorage } from '../lib/safeStorage';
import { clearGoogleHandoff } from '../lib/googleHandoff';
import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const inputClass = 'w-full px-4 py-3 border border-zinc-200 rounded-xl';
const buttonClass = 'w-full py-3 bg-[#1E3A8A] text-white rounded-xl disabled:opacity-50';
const invalidLink = 'Yenileme bağlantısı eksik veya geçersiz. Yeni bağlantı isteyin.';
const errorText = (error, reset) => {
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return reset
    ? 'İşlemin sonucu doğrulanamadı. Yeni şifrenle giriş yapmayı dene; olmazsa yeni bağlantı iste.'
    : 'Sunucu zamanında yanıt vermedi. Birkaç dakika bekleyip e-posta kutnu kontrol et; ardından tekrar deneyebilirsin.';
  if (!error.response) return 'Sunucuya ulaşılamadı. İnternet bağlantını kontrol edip tekrar dene.';
  const detail = error.response.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error.response.status === 422 && Array.isArray(detail)) {
    if (detail.some(item => item.loc?.includes('token'))) return invalidLink;
    if (detail.some(item => item.loc?.includes('email'))) return 'Geçerli bir e-posta adresi yaz.';
    if (detail.some(item => item.loc?.includes('new_password'))) return 'Şifre en az 8 karakter olmalı ve UTF-8 olarak 72 baytı aşmamalı.';
  }
  if (error.response.status === 429) return 'Çok fazla deneme yapıldı. Bir saat sonra tekrar deneyebilirsin.';
  if (error.response.status >= 500) return 'Şifre yenileme servisine şu anda ulaşılamıyor. Lütfen daha sonra tekrar dene.';
  return 'İşlem tamamlanamadı. Lütfen tekrar deneyin.';
};

export default function PasswordRecovery({ reset = false }) {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [googleLink] = useState(() => location.state?.googleLink === true
    || new URLSearchParams(window.location.hash.slice(1)).get('flow') === 'google_link'
    || new URLSearchParams(window.location.search).get('flow') === 'google_link');
  const [token] = useState(() => {
    if (!reset) return '';
    const value = new URLSearchParams(window.location.hash.slice(1)).get('token')
      || new URLSearchParams(window.location.search).get('token') || '';
    // Preserve only a fixed presentation hint, never a supplied redirect URL.
    if (window.location.search.includes('token=')) window.history.replaceState(null, '',
      `/reset-password#token=${encodeURIComponent(value)}${googleLink ? '&flow=google_link' : ''}`);
    return value;
  });
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const validToken = token.length >= 20 && token.length <= 200 && /^[A-Za-z0-9_-]+$/.test(token);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError(''); setMessage('');
    if (reset && (!validToken || password !== repeat)) {
      setError(!validToken ? invalidLink : 'Şifreler eşleşmiyor.'); return;
    }
    if (reset && (password.length < 8 || new TextEncoder().encode(password).length > 72)) {
      setError('Şifre en az 8 karakter olmalı ve UTF-8 olarak 72 baytı aşmamalı.'); return;
    }
    submitting.current = true; setBusy(true);
    try {
      const { data } = await axios.post(`${API}/${reset ? 'reset-password' : 'forgot-password'}`,
        reset ? { token, new_password: password }
          : { email: email.trim(), ...(googleLink ? { flow: 'google_link' } : {}) }, { timeout: 20000 });
      if (data.available === false) { setError(data.message); return; }
      setMessage(data.message);
      if (reset) {
        safeLocalStorage.removeItem('mm_access_token');
        safeLocalStorage.removeItem('mm_refresh_token');
        clearGoogleHandoff();
        window.history.replaceState(null, '', '/reset-password');
        setPassword(''); setRepeat(''); setDone(true);
      }
    } catch (err) { setError(errorText(err, reset)); }
    finally { submitting.current = false; setBusy(false); }
  };

  return <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6 py-10">
    <section className="w-full max-w-sm min-w-0 bg-white rounded-2xl border border-zinc-100 p-6 space-y-5">
      <h1 className="text-2xl font-semibold">{reset ? 'Yeni şifre belirle' : googleLink ? 'MekandaPro şifreni oluştur veya yenile' : 'Şifremi unuttum'}</h1>
      <p className="text-sm text-zinc-600">{reset ? 'En az 8 karakterli yeni bir şifre seç.' : googleLink
        ? 'Google girişinde seçtiğin e-posta adresini yaz. Bağlantıyla bir MekandaPro şifresi belirledikten sonra Google hesabını bağlayabilirsin.'
        : 'Hesabının e-posta adresini yaz. Yenileme bağlantısı 30 dakika geçerli olacak.'}</p>
      {reset && !validToken && !done && !error && <p role="alert" className="text-sm text-red-700">{invalidLink}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {message && <p role="status" className="text-sm text-[#1E3A8A]">{message}</p>}
      {message && !reset && <p className="text-sm text-zinc-600">Gelen kutusu ve spam klasörünü kontrol et. Birden fazla bağlantı istediysen en son geleni kullan. Hesabına kayıtlı e-posta adresini yazdığından emin ol.</p>}
      {!reset && !googleLink && <p className="text-sm text-zinc-600">Bu adresle kayıtlı bir hesap yoksa yenileme e-postası gönderilmez. <Link className="text-[#1E3A8A] underline" to="/register">Hesap oluştur</Link> veya Google ile giriş yaptıysan giriş ekranındaki Google seçeneğini kullan.</p>}
      {!done && <form onSubmit={submit} className="space-y-4" data-testid="password-recovery-form">
        <fieldset disabled={busy} className="space-y-4 min-w-0">
          {reset ? <>
            <label className="block">Yeni şifre<input className={inputClass} aria-label="Yeni şifre" type="password" autoComplete="new-password" required minLength={8} maxLength={128} value={password} onChange={e => setPassword(e.target.value)} /></label>
            <label className="block">Yeni şifre tekrar<input className={inputClass} aria-label="Yeni şifre tekrar" type="password" autoComplete="new-password" required value={repeat} onChange={e => setRepeat(e.target.value)} /></label>
          </> : <label className="block">E-posta<input className={inputClass} aria-label="E-posta" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} /></label>}
          <button className={buttonClass} disabled={busy || (reset && !validToken)} type="submit">{busy ? 'İşleniyor…' : reset ? 'Şifremi yenile' : 'Yenileme bağlantısı iste'}</button>
        </fieldset>
      </form>}
      {reset && !done && <Link className="block text-sm text-[#1E3A8A]" to="/forgot-password" state={{ googleLink }}>Yeni bağlantı iste</Link>}
      {done && googleLink && <div className="space-y-3" data-testid="google-recovery-next">
        <p className="text-sm text-zinc-600">Şifren hazır. Giriş ekranında Google ile devam et, aynı Google hesabını seç ve gerekirse yeni MekandaPro şifreni bir kez gir. Eski Google giriş bağlantısını kullanma.</p>
        <a className="block w-full rounded-xl bg-[#1E3A8A] px-4 py-3 text-center text-white" href="/login">Giriş ekranında Google ile devam et</a>
      </div>}
      {/* Full navigation clears stale AuthContext after revoking the previous session. */}
      <a className="block text-sm text-[#1E3A8A]" href="/login">Giriş ekranına dön</a>
    </section>
  </main>;
}
